import React from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';

import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { formatFareChange } from './fareChange';

/** One day in the rail: the date it stands for, and the cheapest this party can travel that day. */
export interface FareDay {
	/** The day itself. Used as the key and as everything the cell prints. */
	date: Date;
	/** The cheapest party total across the day's sailings, or null where the day sells nothing. */
	price: number | null;
	/**
	 * Whether the route runs at all that day.
	 *
	 * <p>Distinct from a null price, which a day with sailings can also have — an unpriced class, a
	 * window still loading. "No service" and "no price yet" look the same in the rail and are not the
	 * same thing to a booker deciding whether to look at another day.</p>
	 */
	hasTrips: boolean;
	/** A day in the past, or otherwise not open to this booker. */
	disabled: boolean;
	/**
	 * A day that has been and gone — regardless of who is looking.
	 *
	 * <p>Separate from `disabled`, which is about whether this reader may press it: staff may open
	 * a past day and customers may not. Both still need to see at a glance that it is behind them,
	 * so the cell is greyed and says `Past` either way.</p>
	 */
	isPast?: boolean;
	/**
	 * How many sailings the day has.
	 *
	 * <p>What the cell prints instead of a price on a booking type that is not priced here. Freight
	 * is quoted rather than listed, so the old day tabs counted its trips — `3 trips` — and a rail
	 * that printed a fare in that space would be inventing one.</p>
	 */
	tripCount?: number;
	/** Whether this day's sailings carry no fare to show. True for freight. */
	hidePrice?: boolean;
	/**
	 * Whether `price` is a difference against a booking being edited rather than a fare.
	 *
	 * <p>The rail answers "is another day cheaper", and on an edit the honest answer is about the
	 * change, not the fare: the trip is paid for. Carried as a flag rather than as a second field so
	 * the cell cannot draw a fare and a difference at the same time.</p>
	 */
	isChange?: boolean;
}

interface Props {
	days: FareDay[];
	selectedIndex: number;
	onSelect: (index: number) => void;
	/** Moves the window itself, when the booker steps past either end of it. */
	onStep: (forward: boolean) => void;
	backDisabled: boolean;
}

/**
 * The days a booker can choose between, with what each one costs.
 *
 * <p><b>One price per day, not one per class.</b> That is the whole difference between this and the
 * week of tabs it replaces: the tabs carried a cheapest-per-class figure for every day, which is the
 * same three numbers the fare columns below already give for every sailing — the smaller set, in the
 * worse place, a tab the width of a date. A single "from" answers the question a day strip is
 * actually asked, which is "is another day cheaper", and leaves the comparison between classes to
 * the columns that have room for it.</p>
 *
 * <p>It sits inside the table's frame rather than above it, because choosing the day and choosing
 * the sailing are one decision taken in one place.</p>
 */
export default function FareDayRail({ days, selectedIndex, onSelect, onStep, backDisabled }: Props) {
	const intl = useIntl();
	/*
		Every day, always. Five dates shared across 390px are 60px each, which fits neither a day, a
		price, nor a thumb — so the phone used to show a sliding window of three. That traded one
		problem for a worse one: two of the five days were simply not on the screen, and nothing said
		so. The strip now keeps the cells a readable width and scrolls sideways instead, which is
		what a row of dates on a phone is expected to do.
	*/
	const visible = days;
	const start = 0;

	return (
		<div className="fare-rail">
			<button
				type="button"
				className="fare-rail__step"
				data-test-id="fare-rail-earlier-days"
				onClick={() => onStep(false)}
				disabled={backDisabled}
				aria-label={intl.formatMessage({ id: 'trip-option__earlier-days__label', defaultMessage: 'Earlier days' })}
			>
				‹
			</button>

			<div className="fare-rail__days" style={{ '--fare-days': visible.length } as React.CSSProperties}>
				{visible.map((day, offset) => {
					// The index the caller knows the day by, not its position in the window.
					const index = start + offset;
					return (
						<button
							key={day.date.toISOString()}
							type="button"
							data-test-id="fare-rail-day"
							className={classNames('fare-rail__day', {
								'fare-rail__day--on': index === selectedIndex,
								'fare-rail__day--off': day.disabled,
								// A day the route does not run. `hasTrips` has always been on the
								// day; nothing was ever drawing with it, so "No trips" sat on white
								// and read like a price that had not arrived yet.
								'fare-rail__day--empty': !day.hasTrips,
								// Greyed for everyone, pressable only by staff — see `isPast`.
								'fare-rail__day--past': day.isPast,
							})}
							disabled={day.disabled}
							onClick={() => onSelect(index)}
						>
							<span className="fare-rail__date">
								{moment(day.date).format('D')}
								<em>{moment(day.date).format('ddd').toUpperCase()}</em>
							</span>
							{/* eslint-disable-next-line no-nested-ternary */}
							{day.isPast ? (
								/*
									A day that has been and gone. It carried a `From` price like any
									other, which is an offer — and an offer is the one thing a day
									behind the reader cannot be. Keyed off `isPast` rather than
									`disabled` so staff, who are allowed to open these, are told
									what they are opening.
								*/
								<span className="fare-rail__none">
									<FormattedMessage id="trip-option__past__label" defaultMessage="Past" />
								</span>
							) : day.hidePrice ? (
								// Counted, not priced. `No trips` is the same answer the priced cell
								// gives, so an empty day reads the same way on every booking type.
								<span className="fare-rail__none">
									{day.hasTrips ? (
										<FormattedMessage
											id="trip-option__day-trip-count__label"
											defaultMessage="{count, plural, one {# trip} other {# trips}}"
											values={{ count: day.tripCount ?? 0 }}
										/>
									) : (
										<FormattedMessage
											id="trip-option__no-trips__label"
											defaultMessage="No trips"
										/>
									)}
								</span>
							) : day.price === null ? (
								// `No trips` only where the route does not run. A day with sailings but no
								// price yet is still loading, and saying it has no trips is wrong.
								<span className="fare-rail__none">
									{day.hasTrips ? (
										'—'
									) : (
										<FormattedMessage
											id="trip-option__no-trips__label"
											defaultMessage="No trips"
										/>
									)}
								</span>
							) : day.isChange ? (
								// No "From" over a difference: the sign is what the cell is saying,
								// and the word would make it read as a fare again.
								<span className="fare-rail__price fare-rail__price--change">
									{formatFareChange(day.price)}
								</span>
							) : (
								<>
									<span className="fare-rail__from">
										<FormattedMessage
											id="trip-option__from-price__label"
											defaultMessage="From"
										/>
									</span>
									<span className="fare-rail__price">
										{formatPriceDisplay(day.price, false)}
									</span>
								</>
							)}
						</button>
					);
				})}
			</div>

			<button
				type="button"
				className="fare-rail__step"
				data-test-id="fare-rail-later-days"
				onClick={() => onStep(true)}
				aria-label={intl.formatMessage({ id: 'trip-option__later-days__label', defaultMessage: 'Later days' })}
			>
				›
			</button>
		</div>
	);
}
